import {
    CheckCircleOutline as CheckCircleOutlineIcon,
    Close as CloseIcon,
    Error as ErrorIcon,
    Event as EventIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Psychology as PsychologyIcon,
    School as SchoolIcon,
    Warning as WarningIcon,
    WorkOutline as WorkOutlineIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from '@mui/material';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import {
    ENotificationType,
    type INotification,
    type IRemoveNotificationRequest,
} from '@sharedInterfaces/INotification';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { NotificationButton } from '../Core/Header/NotificationButton';

import { BaseWidget } from './BaseWidget';

interface NotificationWidgetProps {
    profilePageStyle?: boolean;
}

/**
 * NotificationWidget displays recent system notifications.
 *
 * @returns {JSX.Element} The rendered NotificationWidget component
 */
function NotificationWidget({ profilePageStyle = false }: NotificationWidgetProps = {}) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [expandedNotifications, setExpandedNotifications] = useState<Set<string | number>>(new Set());

    // Get real notifications from Redux store
    const employeeNotifications = useSelector((state: AppState) => state.notifications.notifications);
    const adminNotifications = useSelector((state: AppState) => state.notifications.adminNotifications);

    // Combine and sort notifications
    const allNotifications = [...employeeNotifications, ...adminNotifications].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );

    // Hide widget if no notifications
    if (allNotifications.length === 0) {
        return null;
    }

    // Map notification type to widget type
    const mapToWidgetType = (notification: INotification): ENotificationType => {
        return notification.notificationType;
    };

    // Format relative time
    const formatRelativeTime = (publishedAt: string): string => {
        const now = new Date();
        const published = new Date(publishedAt);
        const diffMs = now.getTime() - published.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return 'Gerade eben';
        if (diffHours < 24) return `vor ${diffHours} Stunden`;
        if (diffDays === 1) return 'Gestern';
        if (diffDays < 7) return `vor ${diffDays} Tagen`;
        return `vor ${Math.floor(diffDays / 7)} Wochen`;
    };

    const handleNotificationClick = (notificationId: string | number) => {
        setExpandedNotifications((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(notificationId)) {
                newSet.delete(notificationId);
            } else {
                newSet.add(notificationId);
            }
            return newSet;
        });
    };

    const handleDelete = (notificationId: string | number) => {
        const data: IRemoveNotificationRequest = { type: 'delete', id: notificationId };
        WebSocketClient.sendMessage(EWebsocketTopics.NOTIFICATION, data);
    };

    // Funktion zum Abrufen des Icons basierend auf dem Benachrichtigungstyp
    const getNotificationIcon = (type: ENotificationType) => {
        switch (type) {
            case ENotificationType.INFO:
                return <InfoIcon color="info" />;
            case ENotificationType.SUCCESS:
                return <CheckCircleOutlineIcon color="success" />;
            case ENotificationType.WARNING:
                return <WarningIcon color="warning" />;
            case ENotificationType.ERROR:
                return <ErrorIcon color="error" />;
            case ENotificationType.ONBOARDING:
                return <SchoolIcon color="primary" />;
            case ENotificationType.SKILL:
                return <PsychologyIcon color="info" />;
            case ENotificationType.CERTIFICATE:
                return <SchoolIcon color="secondary" />;
            case ENotificationType.PROJECT:
                return <WorkOutlineIcon color="info" />;
            case ENotificationType.MEETING:
                return <EventIcon color="warning" />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    // Funktion zum Abrufen der Farbe basierend auf dem Benachrichtigungstyp
    const getNotificationColor = (type: ENotificationType) => {
        switch (type) {
            case ENotificationType.INFO:
                return theme.palette.info.main;
            case ENotificationType.SUCCESS:
                return theme.palette.success.main;
            case ENotificationType.WARNING:
                return theme.palette.warning.main;
            case ENotificationType.ERROR:
                return theme.palette.error.main;
            case ENotificationType.ONBOARDING:
                return theme.palette.primary.main;
            case ENotificationType.SKILL:
                return theme.palette.info.main;
            case ENotificationType.MEETING:
                return theme.palette.warning.main;
            case ENotificationType.CERTIFICATE:
                return theme.palette.secondary.main;
            case ENotificationType.PROJECT:
                return theme.palette.info.main;
            default:
                return theme.palette.primary.main;
        }
    };

    return (
        <BaseWidget
            sx={
                profilePageStyle
                    ? {
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}05 100%)`,
                          border: `1px solid ${theme.palette.primary.main}20`,
                      }
                    : {}
            }
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    height: { xs: 63, sm: 63 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: profilePageStyle ? 'transparent' : theme.palette.background.paper,
                }}
            >
                <div style={{ marginRight: '8px' }}>
                    <NotificationButton />
                </div>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Benachrichtigungen
                </Typography>
            </Box>

            {/* Notifications List */}
            <Box sx={{ p: 2 }}>
                {allNotifications.slice(0, expanded ? allNotifications.length : 3).map((notification, index) => {
                    const widgetType = mapToWidgetType(notification);
                    const isExpanded = expandedNotifications.has(notification.id);
                    return (
                        <Box
                            key={notification.id}
                            sx={{
                                mb:
                                    index <
                                    (expanded ? allNotifications.length - 1 : Math.min(2, allNotifications.length - 1))
                                        ? 2
                                        : 0,
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderLeft: `4px solid ${getNotificationColor(widgetType)}`,
                                bgcolor: `${getNotificationColor(widgetType)}08`,
                                '&:hover': {
                                    bgcolor: `${getNotificationColor(widgetType)}15`,
                                    boxShadow: 1,
                                },
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onClick={() => handleNotificationClick(notification.id)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        color: getNotificationColor(widgetType),
                                        mt: 0.5,
                                    }}
                                >
                                    {getNotificationIcon(widgetType)}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 0.5,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {notification.title || notification.message}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {formatRelativeTime(notification.publishedAt)}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(notification.id);
                                    }}
                                    sx={{
                                        opacity: 0.6,
                                        '&:hover': { opacity: 1 },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                    <div dangerouslySetInnerHTML={{ __html: notification.messageHTML }} />
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ display: 'block', mt: 1, textAlign: 'right' }}
                                    >
                                        {new Date(notification.publishedAt).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>

            {/* Show more/less button */}
            {allNotifications.length > 3 && (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Button
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                        {expanded ? 'Weniger anzeigen' : 'Alle anzeigen'}
                    </Button>
                </Box>
            )}
        </BaseWidget>
    );
}

export default NotificationWidget;
